import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from "react";
import styles from "./Leaderboard.module.scss";
import { Context } from "../Store";
import { capitalize, trackEvent } from "../utils/helpers";
import { isMobile } from "react-device-detect";

const ordinalSuffix = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const PlayerRow = ({ player, index, score, isExpanded, onToggle, smallRankNums, isLast }) => (
  <>
    <div
      className={`${styles.lbRow} ${index < 3 ? styles[`lbRowRank${index + 1}`] : ""} ${isLast ? styles.lbRowFaded : ""}`}
    >
      <span className={`${styles.lbRowRankNum}${smallRankNums ? ` ${styles.lbRowRankNumSm}` : ""}`}>
        {index + 1}
        <span className={styles.lbRowRankNumSuffix}>{ordinalSuffix(index + 1)}</span>
      </span>

      <div className={styles.lbRowAvatarWrap}>
        <img
          className={styles.lbRowAvatar}
          src={player.photo ? `data:image/jpeg;base64, ${player.photo}` : "./img/boy.png"}
          alt={player.name}
        />
      </div>
      <span className={styles.name}>{capitalize(player.name)}</span>
      <span className={styles.points}>
        {score} <span>Points</span>
      </span>
    </div>
  </>
);

const Leaderboard = ({ visibleCount = 6 }) => {
  const state = React.useContext(Context);

  const [activeTab, setActiveTab] = useState("podium");

  const tabsRef = useRef(null);
  const tabSliderRef = useRef(null);

  const isTournament = state.type == "1" || state.type == "3";

  const updateSlider = (tab) => {
    if (!tabsRef.current || !tabSliderRef.current) return;
    const activeBtn = tabsRef.current.querySelector(`[data-tab="${tab}"]`);
    if (!activeBtn) return;
    tabSliderRef.current.style.width = `${activeBtn.offsetWidth - 1}px`;
    tabSliderRef.current.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
  };

  useEffect(() => {
    updateSlider(activeTab);
  }, [activeTab]);

  useEffect(() => {
    updateSlider("podium");
  }, []);

  const players = Array.isArray(state?.players) ? state.players : [];

  // all_points available for UK stories from 25 Jul 2026 onwards
  const usesAllPoints = !!state?.date && state.date.slice(0, 10) >= "2026-07-25";
  const getScore = (p) =>
    usesAllPoints ? (p.all_points ?? 0) : (p.points || 0) * 2 + (p.draws || 0) - (p.losses || 0);
  // Build a scored+sorted player list equivalent to the original leaderboard
  const parsedPlayers = [...players].sort((a, b) => getScore(b) - getScore(a));

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackEvent("UI Interaction", `Leaderboard Tab: ${tab}`);
  };

  // ── Top 3 for podium ────────────────────────────────────────────────────────
  // final_result 1–6 are explicit ranks; everything else (or if none set) is sorted by points.
  const buildRankedList = (items) => {
    const ranked = items
      .filter((i) => Number(i.final_result) >= 1 && Number(i.final_result) <= 6)
      .sort((a, b) => Number(a.final_result) - Number(b.final_result));
    const unranked = items
      .filter((i) => !(Number(i.final_result) >= 1 && Number(i.final_result) <= 6))
      .sort((a, b) => getScore(b) - getScore(a));
    return ranked.length === 0 ? unranked : [...ranked, ...unranked];
  };

  const buildTop3 = (items) => buildRankedList(items).slice(0, 3);

  const top3 = isTournament ? buildTop3(parsedPlayers) : parsedPlayers.slice(0, 3);

  // ── Leaderboard list rows ────────────────────────────────────────────────────
  // type 3: players sorted by final_result
  // other:  parsedPlayers (sorted by points, existing behaviour)
  const leaderboardRows = isTournament ? buildRankedList(parsedPlayers) : parsedPlayers;

  const [showAll, setShowAll] = useState(false);
  const displayedRows = showAll ? leaderboardRows : leaderboardRows.slice(0, visibleCount);
  const hasMore = leaderboardRows.length > visibleCount;
  const seeMoreBtnRef = useRef(null);
  const savedScrollRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showFade, setShowFade] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useLayoutEffect(() => {
    if (savedScrollRef.current !== null) {
      window.scrollTo(0, savedScrollRef.current);
      savedScrollRef.current = null;
    }
  }, [displayedRows.length]);

  const handleToggleShowAll = () => {
    if (!showAll) savedScrollRef.current = window.scrollY;
    setShowAll((v) => !v);
  };

  useEffect(() => {
    if (!isMobile || activeTab !== "leaderboard") return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowFade(scrollTop + clientHeight < scrollHeight - 10);
    };
    handleScroll();
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [activeTab]);

  // ── Sub-components defined above Leaderboard component ──────────────────────

  // Podium card — classic bar layout
  const rankBarNames = ["first", "second", "third"];

  const PodiumCard = ({ item, rank }) => (
    <div className={`${styles.podiumCol} ${styles[`podiumRank${rank}`]}`}>
      <div className={styles.podiumAvatarWrap}>
        <img
          className={styles.podiumAvatar}
          src={item.photo ? `data:image/jpeg;base64, ${item.photo}` : "./img/boy.png"}
          alt={item.name}
        />
      </div>
      <div className={styles.podiumNameLabel}>{capitalize(item.name)}</div>
      {rank === 1 && <div className={styles.podiumWins}>Wins!</div>}
      <picture>
        <source media="(min-width: 768px)" srcSet={`./img/podium-${rankBarNames[rank - 1]}-desktop.svg`} />
        <img className={styles.podiumBar} src={`./img/podium-${rankBarNames[rank - 1]}.svg`} alt={`${rank} place`} />
      </picture>
    </div>
  );

  return (
    <div className={`${styles.lbSection} section`}>
      <picture className={styles.podiumSpotlight}>
        <source media="(min-width: 768px)" srcSet="./img/spotlight-desktop.svg" />
        <img src="./img/spotlight.svg" alt="" aria-hidden="true" />
      </picture>
      <div className={`${styles.lbStars} ${activeTab !== "leaderboard" ? styles.podiumHidden : ""}`} aria-hidden="true">
        <img src="./img/star-one.svg" className={styles.lbStarOne} alt="" />
        <img src="./img/star-four.svg" className={styles.lbStarTwo} alt="" />
        <img src="./img/star-five.svg" className={styles.lbStarThree} alt="" />
      </div>
      <div className={styles.lbContent}>
        <h2>Who made it</h2>
        <h3>shining like stars</h3>
        {/* Tab switcher */}
        <div className={styles.tabsWrapper}>
          <div className={styles.tabs} ref={tabsRef}>
            <button
              className={`${styles.tab} ${activeTab === "podium" ? styles.tabActive : ""}`}
              onClick={() => handleTabChange("podium")}
              data-tab="podium"
            >
              Podium
            </button>
            <svg xmlns="http://www.w3.org/2000/svg" width="2" height="14" viewBox="0 0 2 14" fill="none">
              <path d="M1 13L1 1" stroke="#886E76" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <button
              className={`${styles.tab} ${activeTab === "leaderboard" ? styles.tabActive : ""}`}
              onClick={() => handleTabChange("leaderboard")}
              data-tab="leaderboard"
            >
              Leaderboard
            </button>
          </div>
        </div>

        {/* Podium view — order: 2nd | 1st | 3rd */}
        <div className={`${styles.podiumWrap} ${activeTab !== "podium" ? styles.podiumHidden : ""}`}>
          <div className={styles.podiumStars}>
            <img src="./img/star-one.svg" className={styles.starOne} alt="star" />
            <img src="./img/star-two.svg" className={styles.starTwo} alt="star" />
            <img src="./img/star-three.svg" className={styles.starThree} alt="star" />
          </div>
          <div className={styles.podium}>
            {top3[1] && <PodiumCard key={top3[1].id} item={top3[1]} rank={2} />}
            {top3[0] && <PodiumCard key={top3[0].id} item={top3[0]} rank={1} />}
            {top3[2] && <PodiumCard key={top3[2].id} item={top3[2]} rank={3} />}
          </div>
        </div>

        {/* Leaderboard view */}
        <div className={`${styles.scrollWrapper} ${activeTab !== "leaderboard" ? styles.podiumHidden : ""}`}>
          <div
            ref={scrollContainerRef}
            className={`${styles.scrollContainer}${isMobile && hasMore ? ` ${styles.scrollContainerCapped}` : ""}`}
          >
            {(isMobile ? leaderboardRows : displayedRows).map((player, index, arr) => (
              <PlayerRow
                key={player.id}
                player={player}
                index={index}
                score={getScore(player)}
                isExpanded={expandedRows.has(player.id)}
                onToggle={toggleRowExpanded}
                smallRankNums={leaderboardRows.length >= 100}
                isLast={!isMobile && hasMore && !showAll && index === arr.length - 1}
              />
            ))}

            {/* Show more/less */}
            {!isMobile && hasMore && (
              <button ref={seeMoreBtnRef} className={styles.seeMoreBtn} onClick={handleToggleShowAll}>
                {showAll ? "Show less" : "Show more"}
              </button>
            )}
          </div>
          {isMobile && hasMore && showFade && <div className={styles.scrollFade} />}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
